import { CircularProgress } from '@mui/material'
import { AnimatePresence, motion } from 'motion/react'
import { cloneElement, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import FluentCheckmark16Filled from '~icons/fluent/checkmark-16-filled'
import FluentLinkMultiple16Filled from '~icons/fluent/link-multiple-16-filled'
import GameIconsCrystalBars from '~icons/game-icons/crystal-bars'
import IcBaselineIosShare from '~icons/ic/baseline-ios-share'
import MaterialSymbolsDownload from '~icons/material-symbols/download'
import MdiChevronDown from '~icons/mdi/chevron-down'
import {
  getPlatformsByLocale,
  sharePlatformMap,
} from 'src/components/SocialShare/SocialShare'
import { UIDialog } from 'src/components/ui/Modal'
import type {
  Artwork,
  TackPreviewFragment,
} from 'src/services/apollo/generated/graphql'
import { getLocale } from 'src/stores/intl'
import { isAnimatedArtwork } from 'src/utils/artwork'

export type ArtworkForShare = Pick<
  Artwork,
  'id' | 'title' | 'media' | 'videoMediaId' | 'type'
> & { tacks?: TackPreviewFragment[] | null }
import { cn } from 'src/utils/cx'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import { getMediaUrl, getPublicUrl, getThumbnailUrl } from 'src/utils/media'
import { track } from 'src/utils/tracker'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import {
  downloadFile,
  getImageDownloadFileExt,
  getImageDownloadFilename,
} from '../utils'
import { useShareAction } from './useShareAction'

const PRIMARY_PLATFORM_COUNT = 4

export default function ArtworkShareContent(props: {
  artwork: ArtworkForShare
  scene?: string
  onClose?: () => void
}) {
  const { artwork, scene } = props
  const locale = getLocale()
  const [expanded, setExpanded] = useState(false)
  const expand = useCallback(() => setExpanded(true), [])

  const {
    shareProps,
    onNativeShare,
    supportsNativeShare,
    sharing,
    copied,
    copyLink,
    countRemains,
    amountEach,
    lastClaimedAmount,
    clearClaimedAmount,
    isLoggedIn,
    pendingRedirect,
    confirmPendingRedirect,
    dismissPendingRedirect,
  } = useShareAction({ artwork, scene })

  const isMobile = useIsMobile()

  // When native share occupies a grid slot, show one fewer primary platform
  const primaryCount = supportsNativeShare
    ? PRIMARY_PLATFORM_COUNT - 1
    : PRIMARY_PLATFORM_COUNT

  const allPlatforms = useMemo(() => getPlatformsByLocale(locale), [locale])
  const primaryPlatforms = useMemo(
    () => allPlatforms.slice(0, primaryCount),
    [allPlatforms, primaryCount],
  )
  const secondaryPlatforms = useMemo(
    () => allPlatforms.slice(primaryCount),
    [allPlatforms, primaryCount],
  )

  return (
    <div className="flex flex-col gap-3 p-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
      {isMobile && <ShareThumbnail artwork={artwork} />}

      {amountEach != null && (isLoggedIn || amountEach > 0) && (
        <ShareRewardStrip
          amountEach={amountEach}
          countRemains={countRemains ?? 0}
          lastClaimedAmount={lastClaimedAmount}
          onClaimedAnimationEnd={clearClaimedAmount}
        />
      )}

      <div className="grid grid-cols-4 gap-0.5">
        {supportsNativeShare && (
          <NativeShareButton
            onClick={onNativeShare}
            disabled={sharing}
            index={0}
          />
        )}
        {primaryPlatforms.map((platform, idx) => (
          <PlatformButton
            key={platform}
            platform={platform}
            shareProps={shareProps(platform)}
            index={idx + (supportsNativeShare ? 1 : 0)}
          />
        ))}
      </div>

      {secondaryPlatforms.length > 0 && !expanded && (
        <ExpandButton onToggle={expand} />
      )}
      {secondaryPlatforms.length > 0 && (
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: [0.32, 0.72, 0, 1],
              }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-4 gap-0.5">
                {secondaryPlatforms.map((platform, idx) => (
                  <PlatformButton
                    key={platform}
                    platform={platform}
                    shareProps={shareProps(platform)}
                    index={idx}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      <div className="h-px bg-black/5 dark:bg-white/5" />

      <div className="flex gap-2">
        <CopyLinkButton copied={copied} onClick={copyLink} />
        {!isMobile && <DownloadButton artwork={artwork} scene={scene} />}
      </div>

      {/* Mobile save guide for shouldCopy platforms (Pixiv, Instagram, etc.) */}
      <SaveImageGuide
        artwork={artwork}
        open={!!pendingRedirect}
        onClose={dismissPendingRedirect}
        redirectLabel={pendingRedirect?.label}
        onRedirect={confirmPendingRedirect}
      />
    </div>
  )
}

function ShareThumbnail({
  artwork,
}: {
  artwork: Pick<Artwork, 'media' | 'title'>
}) {
  const { t } = useTranslation(['common'])
  const thumbnailUrl =
    getThumbnailUrl(artwork.media) ?? getPublicUrl(artwork.media)

  return (
    <div className="flex items-center gap-3">
      <div className="size-12 shrink-0 rounded-lg overflow-hidden bg-black/5 dark:bg-white/5">
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt=""
            className="size-full object-cover object-top"
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold truncate">
          {artwork.title || t('common:untitled')}
        </div>
        <div className="text-xs text-black/40 dark:text-white/40">on PixAI</div>
      </div>
    </div>
  )
}

function ShareRewardStrip(props: {
  amountEach: number
  countRemains: number
  lastClaimedAmount: number | null
  onClaimedAnimationEnd: () => void
}) {
  const { amountEach, countRemains, lastClaimedAmount } = props
  const { t } = useTranslation(['artwork'])
  const totalPips = 3

  return (
    <div className="relative flex items-center gap-2.5 p-2.5 px-3 bg-theme-primary rounded-xl">
      <div className="size-8 shrink-0 rounded-lg bg-white/20 flex items-center justify-center">
        <GameIconsCrystalBars className="text-white text-base" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold leading-tight text-white">
          <Trans
            i18nKey="artwork:share.share-modal.share-to-earn"
            values={{ amount: amountEach }}
            components={{ b: <b className="text-white" /> }}
          />
        </div>
        <div className="text-[11px] text-white/60 mt-0.5">
          {countRemains > 0
            ? t('artwork:share.share-modal.reward-left', {
                count: countRemains,
              })
            : t('artwork:share.share-modal.all-earned')}
        </div>
      </div>
      <div className="flex gap-1.5 shrink-0">
        {Array.from({ length: totalPips }).map((_, i) => {
          const filled = i < totalPips - countRemains
          return (
            <motion.div
              key={i}
              className={cn(
                'size-2 rounded-full border-[1.5px] border-white',
                filled ? 'bg-white opacity-100' : 'bg-transparent opacity-40',
              )}
              animate={
                filled
                  ? { scale: [1, 1.3, 1], opacity: 1 }
                  : { scale: 1, opacity: 0.4 }
              }
              transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
            />
          )
        })}
      </div>

      <AnimatePresence>
        {lastClaimedAmount != null && (
          <motion.div
            className="absolute -top-2 right-4 text-base font-bold text-white pointer-events-none"
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 0, y: -40, scale: 0.8 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1, ease: [0.32, 0.72, 0, 1] }}
            onAnimationComplete={props.onClaimedAnimationEnd}
          >
            +{lastClaimedAmount}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function PlatformButton(props: {
  platform: keyof typeof sharePlatformMap
  shareProps: ReturnType<ReturnType<typeof useShareAction>['shareProps']>
  index: number
}) {
  const { t } = useTranslation(['artwork'])
  const { platform, shareProps: linkProps, index } = props
  const data = sharePlatformMap[platform]
  const label = t(`artwork:share.share-modal.platform.${platform}`, {
    defaultValue: data.label,
  })
  const textColor =
    'textColor' in data && typeof data.textColor === 'string'
      ? data.textColor
      : '#fff'
  const borderColor =
    'borderColor' in data && typeof data.borderColor === 'string'
      ? data.borderColor
      : undefined

  return (
    <motion.a
      className="flex flex-col items-center gap-1.5 py-2.5 rounded-xl cursor-pointer no-underline hover:bg-black/[0.03] dark:hover:bg-white/[0.03] active:bg-black/[0.06] dark:active:bg-white/[0.06]"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.25,
        delay: index * 0.04,
        ease: 'easeOut',
      }}
      {...linkProps}
    >
      <div
        className="size-[46px] rounded-full inline-grid place-items-center transition-transform active:scale-[0.92]"
        style={{
          backgroundColor: data.color,
          color: textColor,
          ...(borderColor
            ? { border: `1px solid ${borderColor}` }
            : { boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.1)' }),
        }}
      >
        {cloneElement(data.icon, { className: 'size-[22px]' })}
      </div>
      <span className="text-[11px] font-medium text-black/50 dark:text-white/50 leading-none text-center">
        {label}
      </span>
    </motion.a>
  )
}

function NativeShareButton(props: {
  onClick: () => void
  disabled: boolean
  index: number
}) {
  const { t } = useTranslation(['artwork'])
  return (
    <motion.button
      type="button"
      className="flex flex-col items-center gap-1.5 py-2.5 rounded-xl cursor-pointer bg-transparent hover:bg-black/[0.03] dark:hover:bg-white/[0.03] active:bg-black/[0.06] dark:active:bg-white/[0.06]"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.25,
        delay: props.index * 0.04,
        ease: 'easeOut',
      }}
      onClick={props.onClick}
      disabled={props.disabled}
    >
      <div className="size-[46px] rounded-full inline-grid place-items-center border-[1.5px] border-dashed border-theme-primary text-theme-primary transition-transform active:scale-[0.92]">
        <IcBaselineIosShare className="size-[22px]" />
      </div>
      <span className="text-[11px] font-medium text-black/50 dark:text-white/50 leading-none">
        {t('artwork:share.share-modal.native-share')}
      </span>
    </motion.button>
  )
}

function ExpandButton(props: { onToggle: () => void }) {
  const { t } = useTranslation(['artwork'])
  return (
    <div className="flex justify-center">
      <button
        type="button"
        className="inline-flex items-center gap-1 px-3.5 py-1.5 rounded-full border border-black/5 dark:border-white/5 bg-black/[0.02] dark:bg-white/[0.02] text-[11px] font-medium text-black/40 dark:text-white/40 cursor-pointer hover:border-black/10 dark:hover:border-white/10 hover:text-black/60 dark:hover:text-white/60 transition-colors"
        onClick={props.onToggle}
      >
        {t('artwork:share.share-modal.more')}
        <MdiChevronDown className="size-3" />
      </button>
    </div>
  )
}

function CopyLinkButton(props: { copied: boolean; onClick: () => void }) {
  const { t } = useTranslation(['artwork'])
  return (
    <button
      type="button"
      className={cn(
        'flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg border text-[13px] font-medium cursor-pointer transition-colors',
        props.copied
          ? 'bg-green-500/10 border-transparent text-green-500'
          : 'border-black/5 dark:border-white/5 bg-black/[0.02] dark:bg-white/[0.02] text-black/60 dark:text-white/60 hover:bg-black/[0.04] dark:hover:bg-white/[0.04] hover:text-black/80 dark:hover:text-white/80',
      )}
      onClick={props.onClick}
    >
      {props.copied ? (
        <FluentCheckmark16Filled className="size-4" />
      ) : (
        <FluentLinkMultiple16Filled className="size-4" />
      )}
      {t('artwork:share.share-modal.copy-link')}
    </button>
  )
}

function DownloadButton(props: {
  artwork: Pick<Artwork, 'id' | 'title' | 'type' | 'media' | 'videoMediaId'>
  scene?: string
}) {
  const { t } = useTranslation(['artwork'])
  const { artwork } = props
  const isAnimated = isAnimatedArtwork(artwork)

  const [loadingDownload, downloadArtwork] = useLoadingCallback(async () => {
    track('share_image_dl_click', { scene: props.scene })

    const imageExt = getImageDownloadFileExt({ artwork, media: artwork.media })
    const imageFileName = `${getImageDownloadFilename({ artwork })}.${imageExt}`

    const url =
      getMediaUrl(artwork.videoMediaId, 'orig') || getPublicUrl(artwork.media)
    if (url) {
      try {
        await downloadFile(url, imageFileName, { skipConversion: isAnimated })
        track('share_image_dl_dialog_show', { scene: props.scene })
      } catch (err: any) {
        toast.error(
          t('artwork:share.watermark-dialog.notice.failed-to-download') +
            ': ' +
            err.message,
        )
      }
    }
  }, [artwork])

  return (
    <button
      type="button"
      disabled={loadingDownload}
      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-black/5 dark:border-white/5 bg-black/[0.02] dark:bg-white/[0.02] text-[13px] font-medium text-black/60 dark:text-white/60 cursor-pointer hover:bg-black/[0.04] dark:hover:bg-white/[0.04] hover:text-black/80 dark:hover:text-white/80 transition-colors disabled:opacity-50"
      onClick={downloadArtwork}
    >
      {loadingDownload ? (
        <CircularProgress size={16} className="text-current" />
      ) : (
        <MaterialSymbolsDownload className="size-4" />
      )}
      {t('artwork:share.share-modal.download')}
    </button>
  )
}

function SaveImageGuide(props: {
  artwork: Pick<Artwork, 'videoMediaId' | 'media'>
  open: boolean
  onClose: () => void
  redirectLabel?: string
  onRedirect?: () => void
}) {
  const { t } = useTranslation(['artwork'])
  const { artwork } = props
  const imageUrl =
    getMediaUrl(artwork.videoMediaId, 'orig') || getPublicUrl(artwork.media)

  return (
    <UIDialog
      open={props.open}
      onClose={props.onClose}
      dismissable
      className="max-w-[90vw] w-fit"
    >
      <div className="flex flex-col items-center gap-4 p-4">
        {imageUrl && (
          <img
            src={imageUrl}
            alt=""
            className="max-h-[50vh] max-w-full rounded-lg object-contain"
          />
        )}
        <p className="text-sm text-black/50 dark:text-white/50 text-center">
          {t('artwork:share.share-modal.long-press-to-save')}
        </p>
        <div className="flex gap-2 w-full">
          {props.onRedirect && props.redirectLabel ? (
            <>
              <button
                type="button"
                className="flex-1 py-2.5 rounded-lg border border-black/5 dark:border-white/5 bg-black/[0.02] dark:bg-white/[0.02] text-[13px] font-medium text-black/60 dark:text-white/60 cursor-pointer"
                onClick={props.onClose}
              >
                {t('artwork:share.share-modal.done')}
              </button>
              <button
                type="button"
                className="flex-1 py-2.5 rounded-lg bg-theme-primary text-white text-[13px] font-medium cursor-pointer"
                onClick={props.onRedirect}
              >
                {t('artwork:share.share-modal.go-to-platform', {
                  platform: props.redirectLabel,
                })}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="flex-1 py-2.5 rounded-lg bg-theme-primary text-white text-[13px] font-medium cursor-pointer"
              onClick={props.onClose}
            >
              {t('artwork:share.share-modal.done')}
            </button>
          )}
        </div>
      </div>
    </UIDialog>
  )
}
